import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { ChatInterface } from "@/components/chat/ChatInterface";
import { FloatingChat } from "@/components/chat/FloatingChat";
import { ServiceGrid } from "@/components/services/ServiceGrid";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Shield, Clock, Star, Building, BarChart3, Calendar } from "lucide-react";
import heroImage from "@/assets/hero-image.jpg";
import { MOCK_COMPANIES, mapSearchTermToCategories } from "@/mock/companies";
import { captureFirstTouchUtm } from "@/lib/utils";
import { useI18n } from "@/i18n/I18nProvider";
import { Seo } from "@/components/seo/Seo";
import { FeaturedServicesCarousel } from "@/components/home/FeaturedServicesCarousel";
import { getSiteUrl } from "@/lib/siteUrl";
import { getCustomerTestimonials, type Testimonial } from "@/lib/testimonials";
import { getPlatformStats, type PlatformStats } from "@/lib/stats";
import { getCityStats, getPopularServicesByCity, type CityStats } from "@/lib/localSeo";
import { isBulkOrderEnabled } from "@/lib/featureFlags";
import { trackClickCta } from "@/lib/analytics";
import BulkOrderInfo from "@/components/b2b/BulkOrderInfo";

// Use centralized mock data (covers all personas/categories)
const mockCompanies = MOCK_COMPANIES;

const Index = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showResults, setShowResults] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [cityTerm, setCityTerm] = useState("");
  const [filteredCompanies, setFilteredCompanies] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [cityStats, setCityStats] = useState<CityStats[]>([]);
  const { t, tx } = useI18n();
  
  // Capture first-touch UTM on landing
  useEffect(() => {
    captureFirstTouchUtm();
  }, []);

  // Fetch testimonials, stats, and city stats
  useEffect(() => {
    (async () => {
      const [customerTestimonials, platformStats, cities] = await Promise.all([
        getCustomerTestimonials(3),
        getPlatformStats(),
        getCityStats(),
      ]);
      setTestimonials(customerTestimonials);
      setStats(platformStats);
      setCityStats(cities);
    })();
  }, []);

  // Handle search from URL parameters (from chat or Local SEO links)
  useEffect(() => {
    const query = searchParams.get('q');
    const city = searchParams.get('city');
    if (query) {
      setSearchTerm(query);
      setShowResults(true);
    }
    if (city) {
      setCityTerm(city);
    }
    if (city) {
      setCityTerm(city);
    }
  }, [searchParams]);

  // Fetch results when user opens the results view
  useEffect(() => {
    if (!showResults) return;
    const term = searchTerm.trim().toLowerCase();
    const cats = mapSearchTermToCategories(term);

    // Expand city search to include nearby locations (helps discovery when vendors haven't filled service_areas yet).
    // This is intentionally small and focused on your priority geography.
    const getCityCandidates = (rawCity: string): string[] => {
      const norm = (s: string) => String(s || "").trim().toLowerCase();
      const key = norm(rawCity);
      if (!key) return [];

      // Aliases / common variants
      const aliasMap: Record<string, string> = {
        "cavallino-treporti": "cavallino",
        "cavallino treporti": "cavallino",
        "venice": "venezia",
      };
      const base = aliasMap[key] || key;

      // Nearby map (edit as needed; ~20–40min radius intents)
      const nearby: Record<string, string[]> = {
        jesolo: ["cavallino", "venezia", "eraclea", "caorle"],
        cavallino: ["jesolo", "venezia"],
        venezia: ["cavallino", "jesolo"],
        eraclea: ["jesolo", "caorle"],
        caorle: ["eraclea", "jesolo", "bibione"],
        bibione: ["caorle"],
      };

      const candidates = [rawCity.trim()];
      for (const n of nearby[base] || []) {
        // Preserve capitalization for display / matching
        const cap = n.charAt(0).toUpperCase() + n.slice(1);
        candidates.push(cap);
      }
      // Deduplicate (case-insensitive)
      const seen = new Set<string>();
      const out: string[] = [];
      for (const c of candidates) {
        const k = norm(c);
        if (!k || seen.has(k)) continue;
        seen.add(k);
        out.push(c);
      }
      return out;
    };

    let isActive = true;
    setLoading(true);
    (async () => {
      try {
        const sb: any = supabase;
        
        // Use enhanced search function if available, otherwise fallback to basic search
        try {
          // Get category IDs from category names
          let categoryIds = null;
          if (cats.length > 0) {
            const { data: categories } = await sb
              .from("service_categories")
              .select("id, name")
              .in("name", cats);
            categoryIds = categories?.map(c => c.id) || null;
          }

        const { data: searchResults, error: searchError } = await sb.rpc(
            'search_companies_by_service_areas',
            {
              search_city: cityTerm.trim() || null,
              search_term: term.trim() || null,
              service_categories: categoryIds,
              limit_count: 50
            }
          );

        if (!searchError && Array.isArray(searchResults) && searchResults.length > 0) {
            const normalized = searchResults.map((c: any) => ({
              id: c.id,
              name: c.company_name,
              image: c.logo_url || "/assets/placeholders/logo-placeholder.svg",
              reviewCount: c.total_reviews || 0,
              rating: c.rating || 4.8,
              location: c.city,
              services: c.matched_services || [],
              badges: [
                ...(c.service_areas_count >= 10 ? ['National Coverage'] : []),
                ...(c.service_areas_count >= 5 ? ['Regional Coverage'] : []),
                ...(c.relevance_score >= 80 ? ['Perfect Match'] : []),
                ...(c.relevance_score >= 60 ? ['Good Match'] : [])
              ],
              availability: "Within 48h",
              relevanceScore: c.relevance_score,
              serviceAreasCount: c.service_areas_count,
              coverage: c.service_areas_coverage
            }));
            
            if (isActive) setFilteredCompanies(normalized);
            return;
          }
        } catch (rpcError) {
          console.log('RPC function not available, using fallback search', rpcError);
        }

        // Fallback to basic search with enhanced service areas support
        let query = sb
          .from("companies")
          .select("id, company_name, city, logo_url, cover_image_url, total_reviews, service_areas, service_areas_count, rating");
        if (cityTerm.trim()) {
          // Search company city (ILIKE) OR service_areas contains the city (JSONB array contains)
          const cities = getCityCandidates(cityTerm.trim());
          const orParts: string[] = [];
          for (const c of cities) {
            const cityJson = JSON.stringify([c]); // -> ["Jesolo"]
            orParts.push(`city.ilike.%${c}%`);
            orParts.push(`service_areas.cs.${cityJson}`);
          }
          query = query.or(orParts.join(','));
        }
        const { data: companies, error: companiesError } = await query.limit(50);
        
        // Debug logging
        console.log('Search query:', {
          cityTerm: cityTerm.trim(),
          searchTerm: term,
          categories: cats,
          companiesFound: companies?.length || 0,
          companiesError,
        });

        const companyIds = (companies || []).map((c: any) => c.id);
        // Recompute categoryIds for fallback branch
        let categoryIds: string[] | null = null;
        if (cats.length > 0) {
          const { data: categories } = await sb
            .from("service_categories")
            .select("id, name")
            .in("name", cats);
          categoryIds = (categories || []).map((c: any) => c.id);
        }
        let services: any[] = [];
        if (term || cats.length > 0) {
          let sQuery = sb
            .from("services")
            .select("id, company_id, name, description, base_price, category_id")
            .in("company_id", companyIds);
          if (term) sQuery = sQuery.or(`name.ilike.%${term}%,description.ilike.%${term}%`);
          if (categoryIds && categoryIds.length > 0) {
            sQuery = sQuery.in("category_id", categoryIds);
          }
          const { data: sData } = await sQuery;
          services = sData || [];
        }

        const byCompany: Record<string, any[]> = {};
        for (const s of services) {
          byCompany[s.company_id] = byCompany[s.company_id] || [];
          byCompany[s.company_id].push({ name: s.name, description: s.description, price: `${s.base_price}` });
        }

        const normalized = (companies || []).map((c: any) => {
          // Calculate basic relevance score
          let relevanceScore = 20;
          if (cityTerm.trim() && c.service_areas && Array.isArray(c.service_areas)) {
            if (c.service_areas.includes(cityTerm.trim())) {
              relevanceScore = 100;
            } else if (c.city.toLowerCase().includes(cityTerm.toLowerCase())) {
              relevanceScore = 80;
            }
          }
          if (c.service_areas_count > 0) relevanceScore += 20;

          return {
            id: c.id,
            name: c.company_name,
            image: c.logo_url || "/assets/placeholders/logo-placeholder.svg",
            reviewCount: c.total_reviews || 0,
            rating: c.rating || 4.8,
            location: c.city,
            services: byCompany[c.id] || [],
            badges: [
              ...(c.service_areas_count >= 10 ? ['National Coverage'] : []),
              ...(c.service_areas_count >= 5 ? ['Regional Coverage'] : []),
              ...(relevanceScore >= 80 ? ['Perfect Match'] : []),
              ...(relevanceScore >= 60 ? ['Good Match'] : [])
            ],
            availability: "Within 48h",
            relevanceScore,
            serviceAreasCount: c.service_areas_count || 0
          };
        }).sort((a, b) => b.relevanceScore - a.relevanceScore);

        // Debug final results
        console.log('Final search results:', {
          normalizedCount: normalized.length,
          normalized: normalized.map(c => ({ id: c.id, name: c.name, location: c.location, relevanceScore: c.relevanceScore, serviceAreasCount: c.serviceAreasCount }))
        });
        
        if (isActive) setFilteredCompanies(normalized);
      } finally {
        if (isActive) setLoading(false);
      }
    })();
    return () => {
      isActive = false;
    };
  }, [showResults, searchTerm, cityTerm]);

  const handleServiceRequest = (request: string) => {
    setSearchTerm(request);
    setShowResults(true);
  };

  const handleCompanySelect = (companyId: string) => {
    navigate(`/company/${companyId}`);
  };

  const handleGetStarted = () => {
    trackClickCta("hero_iniziamo", { section: "hero" });
    navigate("/auth");
  };

  const handleJoinAsCompany = () => {
    trackClickCta("hero_join_company", { section: "hero" });
    navigate("/auth?type=company");
  };

  const features = [
    { icon: Sparkles, title: t('home.features.ai.title','Matching basato su AI'), description: t('home.features.ai.desc','Raccomandazioni intelligenti basate sulle tue esigenze') },
    { icon: Shield, title: t('home.features.verified.title','Professionisti verificati'), description: t('home.features.verified.desc','Tutte le aziende sono controllate') },
    { icon: Clock, title: t('home.features.fast.title','Prenotazione veloce'), description: t('home.features.fast.desc','Prenota servizi in meno di 2 minuti') },
    { icon: Star, title: t('home.features.quality.title','Qualità garantita'), description: t('home.features.quality.desc','Solo professionisti con valutazioni elevate') }
  ];

  if (showResults) {
    return (
      <div className="min-h-screen app-page">
        <Seo
          title={t('home.seo.results_title', 'Find a service near you | Halomora')}
          description={t('home.seo.results_description', 'Search verified companies and book services in a few clicks. Filter by city and category.')}
          canonicalPath="/"
        />
        <div className="container mx-auto px-6 py-8">
          <div className="mb-8">
            <Button
              variant="outline"
              onClick={() => setShowResults(false)}
              className="mb-6"
            >
              {t('home.results.back_to_search', '← Back to Search')}
            </Button>
          </div>
          {loading ? (
            <div className="text-slate-500">{t('home.results.loading', 'Loading results…')}</div>
          ) : (
            <ServiceGrid 
              companies={filteredCompanies}
              searchTerm={searchTerm}
              onCompanySelect={handleCompanySelect}
            />
          )}
        </div>
      </div>
    );
  }

  // Structured Data JSON-LD
  const siteUrl = getSiteUrl();
  const organizationJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Halomora",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "Marketplace per servizi di pulizia e manutenzione. Trova professionisti verificati vicino a te.",
  };

  const webSiteJsonLd = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Halomora",
    "url": siteUrl,
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      // FAQ Generiche
      {
        "@type": "Question",
        "name": "Come funziona Halomora?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Cerca un servizio nella tua zona, confronta aziende verificate, prenota in pochi click e paga online o in contanti. Ricevi conferma immediata e il professionista arriva all'orario concordato."
        }
      },
      {
        "@type": "Question",
        "name": "I professionisti sono verificati?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, tutte le aziende su Halomora sono verificate e hanno recensioni reali dai clienti. Mostriamo solo professionisti con valutazioni elevate e feedback positivi."
        }
      },
      {
        "@type": "Question",
        "name": "Come funziona il pagamento?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Puoi pagare online con carta di credito o in contanti al professionista. I pagamenti online sono sicuri e protetti. Ricevi sempre una ricevuta via email."
        }
      },
      {
        "@type": "Question",
        "name": "Posso cancellare o modificare una prenotazione?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, puoi cancellare o modificare la prenotazione dalla tua area personale. È possibile modificare o annullare fino a 24 ore prima dell'intervento senza costi aggiuntivi. Con meno preavviso possono applicarsi condizioni specifiche."
        }
      },
      {
        "@type": "Question",
        "name": "Quanto tempo ci vuole per prenotare?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "La prenotazione richiede meno di 2 minuti. Scegli il servizio, seleziona data e orario, inserisci i dettagli e conferma. Ricevi conferma immediata. Consigliamo di prenotare con almeno 24–48 ore di anticipo, soprattutto in alta stagione."
        }
      },
      // FAQ Tipologie di servizio
      {
        "@type": "Question",
        "name": "Qual è la differenza tra pulizia di cambio e pulizia di fondo?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "La pulizia di cambio è pensata per il cambio ospite/inquilino: rapida ed efficace, focalizzata su igiene e ordine. La pulizia di fondo è un intervento straordinario più approfondito, indicato dopo lunghi periodi di inutilizzo o quando serve un ripristino completo degli ambienti, compresi angoli e aree normalmente escluse dalla pulizia ordinaria."
        }
      },
      {
        "@type": "Question",
        "name": "Quando è utile il servizio di ripasso e controllo?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Il ripasso è ideale come controllo qualità tra una pulizia e l'altra. Include riordino, rimozione di polvere superficiale e verifica generale dello stato di pulizia, per mantenere sempre un buon livello di presentabilità."
        }
      },
      {
        "@type": "Question",
        "name": "Cosa include il servizio di pulizie straordinarie?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Le pulizie straordinarie sono servizi personalizzati per esigenze specifiche: post-cantiere, dopo traslochi, trattamenti particolari su pavimenti e sanificazioni. Utilizziamo macchinari professionali e prodotti specifici per garantire risultati superiori."
        }
      },
      // FAQ Prezzi e preventivi
      {
        "@type": "Question",
        "name": "Come vengono calcolati i prezzi?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "I prezzi dipendono dalla tipologia di servizio (ripasso, cambio, fondo o straordinario), dalla metratura dell'appartamento e dalle condizioni iniziali degli ambienti. Forniamo sempre un preventivo gratuito e dettagliato prima di iniziare."
        }
      },
      {
        "@type": "Question",
        "name": "I prezzi sono fissi o possono variare?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Per i servizi standard i prezzi sono fissi in base alla tipologia di appartamento. Per le pulizie straordinarie il prezzo è orario (30€/ora) e definito in base alle esigenze specifiche. Tutti i dettagli sono indicati nel preventivo."
        }
      },
      {
        "@type": "Question",
        "name": "È possibile avere uno sconto per servizi ricorrenti?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, per clienti con servizi ricorrenti e frequenti valutiamo condizioni personalizzate. È sufficiente contattarci per trovare la soluzione più adatta."
        }
      },
      // FAQ Prenotazione e organizzazione
      {
        "@type": "Question",
        "name": "Quanto tempo prima devo prenotare?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Consigliamo di prenotare con almeno 24–48 ore di anticipo, soprattutto in alta stagione. Per urgenze verifichiamo sempre la disponibilità."
        }
      },
      {
        "@type": "Question",
        "name": "Operate anche nei weekend e nei giorni festivi?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, operiamo anche nei weekend e nei giorni festivi, previa disponibilità del team. In questi casi potrebbero essere applicate delle maggiorazioni, indicate nel preventivo."
        }
      },
      // FAQ Qualità e materiali
      {
        "@type": "Question",
        "name": "Portate voi i prodotti per la pulizia?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Sì, forniamo tutti i prodotti professionali necessari. Utilizziamo detergenti di alta qualità adatti a ogni superficie. Su richiesta possiamo usare prodotti specifici forniti dal cliente."
        }
      },
      {
        "@type": "Question",
        "name": "Come garantite la qualità del servizio?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Ogni intervento segue procedure standardizzate ed è svolto da operatori qualificati. Su richiesta possiamo fornire un report fotografico finale. L'esperienza e la formazione continua del team garantiscono risultati eccellenti."
        }
      }
    ]
  };

  return (
    <div className="min-h-screen app-page">
      <Seo
        title={t('home.seo.title', 'Halomora | Book cleaning and maintenance services near you')}
        description={t('home.seo.description', 'Find and book home and business services: cleaning, maintenance, laundry, gardening and more.')}
        canonicalPath="/"
        jsonLd={[organizationJsonLd, webSiteJsonLd, faqJsonLd]}
      />
      {/* Hero Section */}
      <section className="app-hero">
        <div className="container mx-auto px-6 py-20">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="mx-auto mb-6 max-w-3xl text-5xl font-bold leading-tight text-white md:text-6xl">
              {t('home.hero.title','Tutto per la tua casa')}
            </h1>
            <p className="mx-auto mb-8 max-w-2xl text-xl leading-relaxed text-slate-200">
              {t('home.hero.subtitle','Tutti i servizi di cui hai bisogno in un unico posto — pulizie, manutenzione, lavanderia, giardinaggio, riparazioni e altro ancora. Prenota professionisti fidati per casa tua o la tua azienda in pochi minuti.')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button 
                variant="hero"
                size="lg"
                onClick={handleGetStarted}
                className="px-10 py-6 text-lg"
              >
                {t('home.hero.cta_start','Iniziamo!')} →
              </Button>
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => {
                  trackClickCta("hero_per_aziende", { section: "hero" });
                  navigate('/for-businesses');
                }}
                className="border-white/25 bg-white/5 px-10 py-6 text-lg font-semibold text-white hover:bg-white/12 hover:text-white"
              >
                {t('home.hero.cta_business','Per le aziende')}
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex flex-wrap justify-center items-center gap-8 text-sm text-slate-200/88">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-accent-green-500" />
                <span>{t('home.trust.professionals','Professionisti fidati')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-accent-orange-500" />
                <span>
                  {stats ? `${stats.averageRating.toFixed(1)}/5` : '4,9/5'} {t('home.trust.rating','Valutazione clienti')}
                  {stats && stats.reviewsCount > 0 && ` ${tx('home.trust.rating_reviews', { count: stats.reviewsCount })}`}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-primary" />
                <span>{t('home.trust.same_day','Prenota lo stesso giorno')}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Search Section (replaces full chatbot) */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('home.search.title','Trova e prenota in pochi secondi')}
              </h2>
              <p className="text-lg text-gray-600">{t('home.search.subtitle','Professionisti selezionati, prezzo chiaro e disponibilità immediata vicino a te.')}</p>
            </div>
            <div className="app-panel p-4 md:p-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <select className="h-12 rounded-[1rem] border border-slate-200/80 bg-white/90 px-4 py-3 text-slate-900 shadow-soft outline-none backdrop-blur-md focus:border-primary/40 focus:ring-4 focus:ring-primary/10" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}>
                  <option value="">{t('home.search.service_placeholder','Seleziona un servizio')}</option>
                  <option value="pulizie">{t('home.search.service.home_cleaning','Pulizie domestiche')}</option>
                  <option value="pulizie uffici">{t('home.search.service.office_cleaning','Pulizie uffici')}</option>
                  <option value="giardinaggio">{t('home.search.service.gardening','Giardinaggio')}</option>
                  <option value="manutenzione">{t('home.search.service.maintenance','Manutenzione')}</option>
                  <option value="lavanderia">{t('home.search.service.laundry','Lavanderia')}</option>
                  <option value="smaltimento ingombranti">{t('home.search.service.disposal','Smaltimento ingombranti')}</option>
                  <option value="disinfestazioni">{t('home.search.service.pest_control','Disinfestazioni')}</option>
                </select>
                <input className="h-12 rounded-[1rem] border border-slate-200/80 bg-white/90 px-4 py-3 text-slate-900 shadow-soft outline-none backdrop-blur-md placeholder:text-slate-400 focus:border-primary/40 focus:ring-4 focus:ring-primary/10" list="cities" placeholder={t('home.search.city_placeholder','Città / zona')} value={cityTerm} onChange={(e) => setCityTerm(e.target.value)} />
                <datalist id="cities">
                  <option value="Jesolo" />
                  <option value="Milano" />
                  <option value="Roma" />
                  <option value="Torino" />
                  <option value="Bologna" />
                  <option value="Padova" />
                  <option value="Venezia" />
                </datalist>
                <select className="h-12 rounded-[1rem] border border-slate-200/80 bg-white/90 px-4 py-3 text-slate-900 shadow-soft outline-none backdrop-blur-md focus:border-primary/40 focus:ring-4 focus:ring-primary/10">
                  <option value="">{t('home.search.when_placeholder','Quando')}</option>
                  <option>{t('home.search.when.today','Oggi')}</option>
                  <option>{t('home.search.when.tomorrow','Domani')}</option>
                  <option>{t('home.search.when.this_week','Questa settimana')}</option>
                </select>
              </div>
              <div className="flex flex-col items-center mt-4 gap-3">
                <p className="text-sm text-gray-500 text-center">
                  {t('home.search.note','Potrai scegliere quando anche più avanti durante la prenotazione.')}
                </p>
                <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                  <Button
                    size="lg"
                    variant="hero"
                    className="w-full sm:w-auto"
                    onClick={() => {
                      trackClickCta("home_search", { section: "search" });
                      setShowResults(true);
                    }}
                  >
                    {t('home.search.cta','Cerca')}
                  </Button>
                  {isBulkOrderEnabled() && (
                    <div className="w-full sm:w-auto flex items-center gap-1.5">
                      <Button
                        size="lg"
                        variant="outline"
                        className="w-full sm:w-auto border-slate-200/80 bg-white/86 text-slate-700 hover:bg-white hover:text-slate-950"
                        onClick={() => {
                          trackClickCta("home_bulk_order", { section: "search" });
                          navigate("/bulk-order");
                        }}
                      >
                        <span className="flex items-center gap-2">
                          BULK ORDER
                          <Badge variant="secondary" className="h-5 px-2 text-[11px] leading-5">
                            beta
                          </Badge>
                        </span>
                      </Button>
                      <BulkOrderInfo
                        className="shrink-0"
                        tooltipText={t('home.search.bulk_tooltip', 'For apartment managers: upload up to 30 orders at once (checkout/check-in).')}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Local SEO - Servizi popolari per città */}
            <div className="mt-8">
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('home.local.title', 'Popular services in your area')}</h3>
                <p className="text-sm text-gray-600">{t('home.local.subtitle', 'Find verified professionals near you')}</p>
              </div>

              {/* City Stats */}
              {cityStats.length > 0 && (
                <div className="mb-6 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                  {cityStats.slice(0, 6).map((city) => (
                    <Card key={city.city} className="border-gray-200 text-center p-3 hover:shadow-md transition-shadow">
                      <div className="text-2xl font-bold text-primary">{city.companiesCount}+</div>
                      <div className="text-xs text-gray-600 mt-1">{tx('home.local.companies_in', { city: city.city })}</div>
                    </Card>
                  ))}
                </div>
              )}

              {/* Popular Services Links */}
              <div className="flex flex-wrap items-center justify-center gap-2">
                {getPopularServicesByCity().slice(0, 8).map((item, idx) => (
                  <Button 
                    key={idx}
                    variant="outline" 
                    size="sm" 
                    onClick={() => {
                      trackClickCta("home_seo_service_city", { section: "local_links", path: item.url });
                      navigate(item.url);
                    }}
                    className="bg-white/86 hover:bg-white hover:text-slate-950 transition-colors"
                  >
                    {item.serviceName} {item.city}
                  </Button>
                ))}
              </div>
            </div>

            <Card className="mt-8 border-primary/20 bg-gradient-to-r from-primary/5 to-accent-green-500/5 shadow-card">
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="max-w-2xl">
                    <div className="text-sm font-medium text-primary mb-1">{t('home.apartment_mgr.eyebrow', 'For apartment managers')}</div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {t('home.apartment_mgr.title', 'Still missing a continuous cleaning partner for the season?')}
                    </h3>
                    <p className="text-gray-600 mt-2">
                      {t('home.apartment_mgr.desc', "If you're already struggling to find reliable coverage in tourist areas, Halomora can help you build a long-term relationship with reputable companies and connect it to in-app operational flows.")}
                    </p>
                  </div>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button onClick={() => {
                      trackClickCta("home_apartment_manager", { section: "apartment_manager" });
                      navigate("/apartment-manager/partner-pulizie");
                    }}>
                      {t('home.apartment_mgr.cta_discover', 'Discover the service')}
                    </Button>
                    <Button variant="outline" onClick={() => {
                      trackClickCta("home_bulk_order_secondary", { section: "apartment_manager" });
                      navigate("/bulk-order");
                    }}>
                      {t('home.apartment_mgr.cta_bulk', 'Go to Bulk Order')}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured Services Carousel */}
      <section className="border-t border-slate-200/70 bg-white/68 py-14 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3">
                {t('home.featured.title', 'Featured services from our most active partners')}
              </h2>
              <p className="text-lg text-gray-600">
                {t('home.featured.subtitle', 'A selection of popular, reliable services: clear pricing, availability and one-click booking.')}
              </p>
            </div>
            <FeaturedServicesCarousel />

            <div className="mt-10 text-center">
              <div className="text-sm text-gray-600 mb-3">
                {t('home.featured.pro_pitch', 'Are you a service company? Get more visibility and featured placement with PRO.')}
              </div>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Button size="lg" variant="hero" onClick={() => {
                  trackClickCta("featured_diventa_pro", { section: "featured_services" });
                  navigate("/for-businesses");
                }}>
                  {t('home.featured.cta_pro', 'Become PRO')}
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="border-slate-200/80 bg-white/86 text-slate-700 hover:bg-white"
                  onClick={() => {
                    trackClickCta("featured_scopri_piani", { section: "featured_services" });
                    navigate("/for-businesses");
                  }}
                >
                  {t('home.featured.cta_plans', 'Explore plans')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      {testimonials.length > 0 && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="text-center mb-8">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">{t('home.testimonials.title', 'What our customers say')}</h2>
              <p className="text-lg text-gray-600">{t('home.testimonials.subtitle', 'Real reviews from customers who booked on Halomora')}</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {testimonials.map((review) => (
                <Card key={review.id} className="border-gray-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-2 mb-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          className={`h-4 w-4 ${
                            i < review.rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>
                    <p className="text-gray-700 mb-4 italic">"{review.quote}"</p>
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <span className="text-primary font-semibold">{review.initials || "CV"}</span>
                      </div>
                      <div>
                        <div className="font-semibold">{review.name || t('home.testimonials.verified_client', 'Verified customer')}</div>
                        {review.city && review.service && (
                          <div className="text-sm text-gray-500">{review.city} • {review.service}</div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.features.title','Perché le aziende amano Halomora')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.features.subtitle','Vivi un\'esperienza di prenotazione senza problemi, lavora con professionisti fidati e goditi una qualità garantita per tutte le tue esigenze di pulizia aziendale.')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center group">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 text-primary rounded-2xl mb-4 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Benefits Section */}
      <section className="py-20 bg-white/68 backdrop-blur-sm">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {t('home.provider.title','Sei un fornitore di servizi?')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('home.provider.subtitle','Unisciti alla nostra piattaforma per far crescere la tua attività con strumenti intelligenti e clienti affidabili.')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="app-panel p-8">
              <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="h-6 w-6 text-primary" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.provider.dashboard.title','Dashboard intelligente')}</h3>
              <p className="text-gray-600">{t('home.provider.dashboard.desc','Gestisci prenotazioni, traccia i guadagni e fai crescere la tua attività con la nostra dashboard intelligente.')}</p>
            </div>
            
            <div className="app-panel p-8">
              <div className="w-12 h-12 bg-accent-green-500/10 rounded-xl flex items-center justify-center mb-4">
                <Building className="h-6 w-6 text-accent-green-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.provider.pricing.title','Prezzi basati su AI')}</h3>
              <p className="text-gray-600">{t('home.provider.pricing.desc','Ottimizza i tuoi prezzi con insights basati su AI e analisi di mercato.')}</p>
            </div>
            
            <div className="app-panel p-8">
              <div className="w-12 h-12 bg-accent-orange-500/10 rounded-xl flex items-center justify-center mb-4">
                <Calendar className="h-6 w-6 text-accent-orange-500" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('home.provider.scheduling.title','Pianificazione facile')}</h3>
              <p className="text-gray-600">{t('home.provider.scheduling.desc','Gestione del calendario semplificata e conferme di prenotazione automatizzate.')}</p>
            </div>
          </div>
          
          <div className="mt-12 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Button 
              variant="hero"
              size="lg"
              onClick={handleJoinAsCompany}
              className="text-lg px-8 py-4"
            >
              {t('home.provider.cta','Inizia a far crescere la tua attività')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate("/for-businesses/booking-system-demo")}
              className="text-lg px-8 py-4"
            >
              Demo booking system
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">{t('home.faq.title', 'Frequently Asked Questions')}</h2>
              <p className="text-lg text-gray-600">{t('home.faq.subtitle', 'Everything you want to know about our services')}</p>
            </div>

            <div className="space-y-4">
              {[
                {
                  catKey: 'home.faq.cat.types',
                  catFallback: 'Service types',
                  items: ['types_diff', 'refresh', 'extra'],
                },
                {
                  catKey: 'home.faq.cat.pricing',
                  catFallback: 'Pricing & quotes',
                  items: ['price_calc', 'price_fixed', 'discount'],
                },
                {
                  catKey: 'home.faq.cat.booking',
                  catFallback: 'Booking & scheduling',
                  items: ['advance', 'cancel', 'weekend'],
                },
                {
                  catKey: 'home.faq.cat.quality',
                  catFallback: 'Quality & materials',
                  items: ['products', 'quality'],
                },
                {
                  catKey: 'home.faq.cat.general',
                  catFallback: 'General',
                  items: ['how', 'verified', 'payment'],
                },
              ].map((cat) => (
                <div key={cat.catKey} className="bg-white rounded-2xl p-6 shadow-card border border-gray-200">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">{t(cat.catKey, cat.catFallback)}</h3>
                  <div className="space-y-4">
                    {cat.items.map((slug) => (
                      <details key={slug} className="rounded-lg border border-gray-200 p-4 bg-gray-50">
                        <summary className="cursor-pointer font-medium text-gray-900">{t(`home.faq.q.${slug}_q`, slug)}</summary>
                        <div className="mt-2 text-gray-600">{t(`home.faq.q.${slug}_a`, '')}</div>
                      </details>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FloatingChat />
    </div>
  );
};

export default Index;